import { type Aircraft, isAirborne } from "../aircraft/Aircraft";
import { distanceSquared } from "./geometry";

export interface CollisionOptions {
  /** Aircraft younger than this are immune (they are still fading in). */
  readonly graceSeconds: number;
  /** A departure counts as traffic past this take-off progress. */
  readonly departureAirborneProgress: number;
  /** Warning radius as a multiple of the summed body radii. */
  readonly warningFactor: number;
}

function airborneTraffic(
  aircraft: readonly Aircraft[],
  options: CollisionOptions,
): Aircraft[] {
  return aircraft.filter(
    (item) =>
      isAirborne(item, options.departureAirborneProgress) &&
      item.age >= options.graceSeconds,
  );
}

/** First colliding pair, or null. Bodies touch when the radii overlap. */
export function findCollision(
  aircraft: readonly Aircraft[],
  options: CollisionOptions,
): readonly [Aircraft, Aircraft] | null {
  const traffic = airborneTraffic(aircraft, options);
  for (let i = 0; i < traffic.length; i++) {
    for (let j = i + 1; j < traffic.length; j++) {
      const a = traffic[i]!;
      const b = traffic[j]!;
      const contact = a.spec.radius + b.spec.radius;
      if (distanceSquared(a.pos, b.pos) <= contact * contact) {
        return [a, b];
      }
    }
  }
  return null;
}

/**
 * How bad each warned aircraft's closest encounter is, keyed by id.
 *
 * 0 is the outer edge of the warning radius and 1 is the moment of contact,
 * so the renderer can draw "how close" rather than only "close". Aircraft with
 * nothing near them are absent, which makes the keys the warned set.
 *
 * Two departures are never warned against each other — they are separated by
 * the runway scheduler instead.
 */
export function proximityLevels(
  aircraft: readonly Aircraft[],
  options: CollisionOptions,
): Map<number, number> {
  const traffic = airborneTraffic(aircraft, options);
  const levels = new Map<number, number>();
  for (let i = 0; i < traffic.length; i++) {
    for (let j = i + 1; j < traffic.length; j++) {
      const a = traffic[i]!;
      const b = traffic[j]!;
      if (a.state === "departing" && b.state === "departing") {
        continue;
      }
      const contact = a.spec.radius + b.spec.radius;
      const warning = contact * options.warningFactor;
      const separation = distanceSquared(a.pos, b.pos);
      if (separation <= contact * contact || separation > warning * warning) {
        continue;
      }
      // Linear in distance, not in its square: the ring has to close at the
      // rate the gap does, or it reads as easing rather than as danger.
      const gap = Math.sqrt(separation) - contact;
      const span = warning - contact;
      const level = span > 1e-6 ? 1 - Math.min(1, Math.max(0, gap / span)) : 1;
      levels.set(a.id, Math.max(levels.get(a.id) ?? 0, level));
      levels.set(b.id, Math.max(levels.get(b.id) ?? 0, level));
    }
  }
  return levels;
}

/**
 * Ids of aircraft that are close enough to warn about but not yet touching.
 * The severity behind them is `proximityLevels`.
 */
export function findProximityWarnings(
  aircraft: readonly Aircraft[],
  options: CollisionOptions,
): Set<number> {
  return new Set(proximityLevels(aircraft, options).keys());
}
